import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, LinearProgress, Typography } from '@mui/material';
import { Components } from './styled';
import { useTranscriber, TranscriberStage, Transcriber } from '../../../hooks/useTranscriber';
import { normalizeLrc } from '../../../utils/transcript/lrc';
import { formatEtaSeconds, formatMegabytes, formatPercent } from '../../../utils/transcript/progressFormat';
import ConfirmationModal from '../ConfirmationModal';
import useModalState from '../../../hooks/useModalState';

const STAGE_LABELS: Record<TranscriberStage, string> = {
  /* eslint-disable @typescript-eslint/naming-convention */
  'idle': 'Idle',
  'loading-model': 'Loading model…',
  'downloading-audio': 'Downloading audio…',
  'decoding': 'Decoding audio…',
  'transcribing': 'Transcribing…',
  'done': 'Done',
  'error': 'Error',
  /* eslint-enable @typescript-eslint/naming-convention */
};

interface LessonTranscriptSectionProps {
  audioFilePath: string | undefined;
  isAudioUploading: boolean;
  lrcTranscript: string;
  onChangeLrcTranscript: (value: string) => void;
  onGenerated: (lrc: string) => void;
  isModalOpen: boolean;
  /**
   * Changing this to a new truthy value (e.g. a lesson id) starts a generation
   * run as soon as the section is ready, skipping the replace-confirmation
   * prompt. Used by callers that open the modal directly into "Generate" mode.
   */
  autoGenerateToken?: string;
  /**
   * When provided, the section uses this transcriber instance instead of
   * creating its own worker/pipeline. Used by `TranscriptReviewModal` so the
   * Transcripts screen's batch transcriber and the modal share one worker.
   */
  transcriber?: Transcriber;
}

const LessonTranscriptSection = ({
  audioFilePath,
  isAudioUploading,
  lrcTranscript,
  onChangeLrcTranscript,
  onGenerated,
  isModalOpen,
  autoGenerateToken,
  transcriber,
}: LessonTranscriptSectionProps) => {
  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const [localError, setLocalError] = useState<string | undefined>();
  const replaceConfirmModal = useModalState<boolean>();
  // Tracks the audio path a running/most recent transcription targeted, so we can
  // detect the admin swapping the audio file mid-run and cancel the stale job.
  const runningAudioPathRef = useRef<string | undefined>();
  // Tracks whether this section itself started the currently-active job on a
  // shared transcriber, so its cleanup/close effects never cancel work (e.g. a
  // batch run) that some other owner started.
  const ownsActiveJobRef = useRef(false);

  const shouldPreload = Boolean(isModalOpen && audioFilePath);
  const local = useTranscriber({ preload: !transcriber && shouldPreload });
  const {
    stage,
    progress,
    device,
    error,
    modelLoadProgress,
    downloadProgress,
    transcribeProgress,
    transcribe,
    cancel,
  } = transcriber ?? local;

  const isSharedBusy = stage !== 'idle' && stage !== 'done' && stage !== 'error';
  const isBusy = transcriber ? ownsActiveJobRef.current && isSharedBusy : isSharedBusy;

  const cancelOwnJob = () => {
    if (!transcriber || ownsActiveJobRef.current) {
      cancel();
    }
  };

  useEffect(() => {
    // Cancel any running/preloaded job the moment the modal closes so the worker
    // isn't kept warm (or transcribing) while the modal is hidden. When using a
    // shared transcriber, only cancel if this section owns the active job.
    if (!isModalOpen) {
      cancelOwnJob();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  useEffect(() => {
    // If the admin swaps/clears the audio file while a job targeting the previous
    // path is running, cancel it - the in-flight result would no longer be valid.
    if (isBusy && runningAudioPathRef.current && runningAudioPathRef.current !== audioFilePath) {
      cancelOwnJob();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioFilePath]);

  useEffect(() => {
    return () => {
      cancelOwnJob();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runGenerate = async () => {
    if (!audioFilePath) {
      return;
    }
    setSuccessMessage(undefined);
    setLocalError(undefined);
    runningAudioPathRef.current = audioFilePath;
    ownsActiveJobRef.current = true;

    try {
      const result = await transcribe(audioFilePath);
      const normalized = normalizeLrc(result.lrc);
      onGenerated(normalized);
      setSuccessMessage(
        `Generated in ${Math.round(result.timingsMs.total / 1000)} s (${result.device}) — review before saving.`,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message !== 'Transcription cancelled') {
        setLocalError(message);
      }
    } finally {
      runningAudioPathRef.current = undefined;
      ownsActiveJobRef.current = false;
    }
  };

  const onGenerateClick = () => {
    if (lrcTranscript?.trim()?.length) {
      replaceConfirmModal.open(true);
      return;
    }
    void runGenerate();
  };

  const onConfirmReplace = () => {
    void runGenerate();
  };

  const onCancel = () => {
    cancelOwnJob();
  };

  const consumedAutoGenerateTokenRef = useRef<string | undefined>();

  useEffect(() => {
    if (
      autoGenerateToken &&
      autoGenerateToken !== consumedAutoGenerateTokenRef.current &&
      isModalOpen &&
      audioFilePath &&
      !isAudioUploading &&
      !isBusy
    ) {
      consumedAutoGenerateTokenRef.current = autoGenerateToken;
      void runGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerateToken, isModalOpen, audioFilePath, isAudioUploading, isBusy]);

  const isGenerateDisabled = !audioFilePath || isAudioUploading || isBusy;

  const phaseText = (() => {
    if (stage === 'downloading-audio' && downloadProgress) {
      const eta = formatEtaSeconds(downloadProgress.etaSeconds);
      return `Downloading ${formatPercent(downloadProgress.percent)}${eta ? ` · ${eta}` : ''}`;
    }
    if (stage === 'transcribing' && transcribeProgress) {
      const eta = formatEtaSeconds(transcribeProgress.etaSeconds);
      const percentLabel = `${transcribeProgress.estimated ? '~' : ''}${formatPercent(transcribeProgress.percent)}`;
      return `Transcribing ${percentLabel}${eta ? ` · ${eta}` : ''}`;
    }
    const modelLoadSuffix =
      stage === 'loading-model' && modelLoadProgress !== null ? ` ${formatPercent(modelLoadProgress)}` : '';
    return `${STAGE_LABELS[stage]}${modelLoadSuffix}`;
  })();

  const phaseSecondaryText =
    stage === 'downloading-audio' && downloadProgress && downloadProgress.totalBytes > 0
      ? `${formatMegabytes(downloadProgress.loadedBytes)} / ${formatMegabytes(downloadProgress.totalBytes)}`
      : undefined;

  const determinateProgress =
    stage === 'transcribing' && transcribeProgress
      ? transcribeProgress.percent
      : stage === 'downloading-audio' && downloadProgress
      ? downloadProgress.percent
      : progress;

  const disabledReason = !audioFilePath
    ? 'Upload an audio file before generating a transcript.'
    : isAudioUploading
    ? 'Wait for the audio upload to finish.'
    : isBusy
    ? 'A transcription is already running.'
    : undefined;

  return (
    <>
      <Components.FieldWrapper>
        <Components.FieldLabel>LRC Transcript</Components.FieldLabel>
        <Components.TranscriptActionsRow>
          <Button variant={'outlined'} size={'small'} disabled={isGenerateDisabled} onClick={onGenerateClick}>
            Generate transcript
          </Button>
          {isBusy && (
            <Button variant={'text'} size={'small'} onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Components.TranscriptActionsRow>
        {disabledReason && <Components.FieldHint>{disabledReason}</Components.FieldHint>}

        {isBusy && (
          <Components.TranscriptActionsRow>
            <Typography variant={'caption'} color={'text.secondary'}>
              {phaseText}
              {device ? ` (device: ${device})` : ''}
            </Typography>
          </Components.TranscriptActionsRow>
        )}
        {isBusy && phaseSecondaryText && (
          <Typography variant={'caption'} color={'text.secondary'}>
            {phaseSecondaryText}
          </Typography>
        )}
        {isBusy && (
          <LinearProgress
            sx={{ width: '100%' }}
            variant={determinateProgress !== null ? 'determinate' : 'indeterminate'}
            value={determinateProgress ?? 0}
          />
        )}

        {successMessage && (
          <Typography variant={'caption'} color={'success.main'}>
            {successMessage}
          </Typography>
        )}
        {(localError || error) && <Alert severity={'error'}>{localError || error}</Alert>}

        <Components.TextArea
          value={lrcTranscript}
          onChange={(event) => onChangeLrcTranscript(event.target.value)}
          name={'lrcTranscript'}
          placeholder={'Paste LRC transcript content here'}
          disabled={isBusy}
        />
      </Components.FieldWrapper>

      <ConfirmationModal
        {...replaceConfirmModal}
        modalTitle={'Replace transcript?'}
        confirmationText={'This lesson already has a transcript. Generating a new one will replace its content.'}
        onConfirm={onConfirmReplace}
      />
    </>
  );
};

export default LessonTranscriptSection;
